import React, { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { Activity, LogIn } from 'lucide-react'
import { useAuth } from '../context/AuthContext'

export default function Login() {
  const { login, isAuthenticated } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error,    setError]    = useState('')
  const [loading,  setLoading]  = useState(false)

  if (isAuthenticated) return <Navigate to="/" replace />

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!username || !password) return
    setLoading(true)
    setError('')
    const result = await login(username, password)
    if (!result.success) {
      setError(result.error || 'Invalid credentials')
    }
    setLoading(false)
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ background: 'linear-gradient(135deg, #060d1a 0%, #0d1321 60%, #0a1628 100%)' }}
    >
      <div className="w-full max-w-sm px-4">

        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <div
            className="flex items-center justify-center w-12 h-12 rounded-xl mb-4"
            style={{
              background: 'rgba(34,211,238,0.08)',
              border: '1px solid rgba(34,211,238,0.2)',
              boxShadow: '0 0 30px rgba(34,211,238,0.1)',
            }}
          >
            <Activity className="h-6 w-6 text-cyan-400" />
          </div>
          <h1
            className="text-white font-bold tracking-widest"
            style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: '1.4rem', letterSpacing: '0.2em' }}
          >
            SAFERFUNDS
          </h1>
          <p
            className="text-slate-600 mt-1"
            style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem', letterSpacing: '0.1em' }}
          >
            TRADING PLATFORM
          </p>
        </div>

        {/* Card */}
        <div
          className="rounded-2xl p-8"
          style={{
            background: '#111827',
            border: '1px solid #1e293b',
            boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
          }}
        >
          <h2
            className="text-slate-400 mb-6 uppercase font-semibold"
            style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem', letterSpacing: '0.14em' }}
          >
            Sign In
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                className="block text-slate-500 mb-1.5"
                style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem', letterSpacing: '0.08em' }}
              >
                USERNAME
              </label>
              <input
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                autoComplete="username"
                autoFocus
                className="w-full rounded-lg px-4 py-3 text-slate-200 focus:outline-none transition-all"
                style={{
                  background: '#0f172a',
                  border: '1px solid #374151',
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: '0.85rem',
                }}
                onFocus={e => e.target.style.borderColor = 'rgba(34,211,238,0.4)'}
                onBlur={e => e.target.style.borderColor = '#374151'}
              />
            </div>

            <div>
              <label
                className="block text-slate-500 mb-1.5"
                style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem', letterSpacing: '0.08em' }}
              >
                PASSWORD
              </label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
                className="w-full rounded-lg px-4 py-3 text-slate-200 focus:outline-none transition-all"
                style={{
                  background: '#0f172a',
                  border: '1px solid #374151',
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: '0.85rem',
                }}
                onFocus={e => e.target.style.borderColor = 'rgba(34,211,238,0.4)'}
                onBlur={e => e.target.style.borderColor = '#374151'}
              />
            </div>

            {error && (
              <div
                className="rounded-lg px-4 py-2.5 text-xs"
                style={{ background: 'rgba(244,63,94,0.07)', border: '1px solid rgba(244,63,94,0.2)', color: '#fb7185', fontFamily: 'JetBrains Mono, monospace' }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !username || !password}
              className="w-full flex items-center justify-center gap-2 rounded-lg py-3 font-semibold transition-all mt-2"
              style={{
                background: (loading || !username || !password) ? '#1e293b' : '#1d4ed8',
                color:      (loading || !username || !password) ? '#475569' : '#bfdbfe',
                border:     (loading || !username || !password) ? '1px solid #374151' : '1px solid rgba(59,130,246,0.4)',
                boxShadow:  (loading || !username || !password) ? 'none' : '0 0 20px rgba(59,130,246,0.2)',
                fontFamily: 'JetBrains Mono, monospace',
                fontSize:   '0.75rem',
                letterSpacing: '0.1em',
                cursor:     (loading || !username || !password) ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <LogIn className="h-3.5 w-3.5" />
              {loading ? 'AUTHENTICATING...' : 'SIGN IN'}
            </button>
          </form>
        </div>

        <p
          className="text-center text-slate-800 mt-6"
          style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem' }}
        >
          Saferfunds v3.5 · Paper Trading
        </p>
      </div>
    </div>
  )
}
